import React, { FormEvent, useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import ModalDialog from 'components/modal-dialog/ModalDialog';
import { CommunityUser } from '@cocoplatform/coco-rtc-shared';
import LoadingButton from '@mui/lab/LoadingButton';
import ControlLabel from 'components/control-label/ControlLabel';
import C from './username-edit-modal.css';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import { reportServerError } from 'utils/report-error';
import { useCommunityDetails } from 'atoms/community-details';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const UsernameEditModal = (p: {
  communityId: string;
  user: CommunityUser;
  onClose?: () => void;
}) => {
  const { _ } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState({
    id: p.user.id,
    password: '',
    username: p.user.username ?? '',
  });
  const [groups, setGroups] = useState('');
  const { community, patchCommunityDetails } = useCommunityDetails({
    communityId: p.communityId,
  });
  useEffect(() => {
    const groups =
      community?.groups
        ?.filter((it) => it.userIds.indexOf(p.user.id) >= 0)
        ?.map((it) => it.name) ?? [];
    setGroups(groups.join(', '));
  }, [community?.groups, p.user.id]);

  return (
    <ModalDialog
      isCloseable
      onClose={p.onClose}
      title={_(msg`Edit Details`)}
      classNames={{
        title: C.dialogTitle,
      }}
    >
      <form
        onSubmit={handleSubmit}
        className={C.primaryForm}
        style={{ textAlign: 'left', paddingTop: '10px', marginTop: 0 }}
        autoComplete='off'
      >
        <input type='hidden' name='id' value={p.user.id} />
        <ControlLabel htmlFor='username'>
          <Trans>Username</Trans>
        </ControlLabel>
        <TextField
          id='username'
          name='username'
          variant='filled'
          placeholder={_(msg`Username`)}
          className={C.filledInput}
          value={user.username}
          onChange={(e) => {
            const username = e.currentTarget.value;
            setUser((d) => ({ ...d, username }));
          }}
          style={{ flexGrow: 1 }}
        />
        <ControlLabel htmlFor='password_field' style={{ paddingTop: '10px' }}>
          <Trans>Password</Trans>
        </ControlLabel>
        <TextField
          name='password'
          id='password_field'
          variant='filled'
          placeholder={_(msg`Password`)}
          value={user.password}
          onChange={(e) => {
            const password = e.currentTarget.value;
            setUser((d) => ({ ...d, password }));
          }}
          style={{ flexGrow: 1 }}
          className={C.filledInput}
          type='password'
        />
        <ControlLabel htmlFor='groups_field' style={{ paddingTop: '10px' }}>
          <Trans>Group/s</Trans>
        </ControlLabel>
        <TextField
          id='groups_field'
          name='groups'
          variant='filled'
          placeholder={_(msg`Group/s`)}
          value={groups}
          onChange={(e) => {
            const groups = e.currentTarget.value;
            setGroups(groups);
          }}
          style={{ flexGrow: 1 }}
          className={C.filledInput}
        />

        <div style={{ marginTop: '10px' }}>
          <LoadingButton variant='contained' type='submit' loading={submitting}>
            <Trans>Update</Trans>
          </LoadingButton>
        </div>
      </form>
    </ModalDialog>
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.stopPropagation();
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData();
    formData.append(
      'users',
      JSON.stringify({
        users: [
          {
            ...user,
            groups: groups
              .split(',')
              .map((it) => it.trim())
              .filter(Boolean),
          },
        ],
      }),
    );
    try {
      await httpClient.post(
        `/community/${p.communityId}/login-allowances`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      patchCommunityDetails((c) => ({
        ...c,
        members: c.members?.map((m) => {
          if (m.user?.id !== user.id) return m;
          return { ...m, user: { ...m.user, ...user } };
        }),
      }));
      p.onClose?.();
    } catch (e: any) {
      reportServerError(e);
    }
  }
};

export default UsernameEditModal;
